import { noop } from 'lodash';
import { useMemo } from 'react';

import {
   HealthcareProviderNotificationSummary,
   HealthcareProviderNotificationSummaryQuery,
} from '@dreryk/egabinet-phone-notification-frontend-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { monthYearFormatter, SortOrder, Translate } from '@dreryk/react-common';
import {
   EmptyStatePlaceholder,
   HorizontalAlign,
   JustifyContent,
   StyledMaxWidth,
   Table,
   TableColumnConfiguration,
   ValueRendererProps,
} from '@dreryk/react-components';

import { patientPhoneNotificationTranslationKeys } from '../../translations';

import { StyledPatientPhoneNotificationHeader } from './PatientPhoneNotificationHeader';

type YearMonthFormatterProps = {
   readonly value: Date | null;
   readonly children?: (value: string | null) => JSX.Element;
};

const YearMonthFormatter = (props: YearMonthFormatterProps): JSX.Element => {
   const value = monthYearFormatter(props.value);
   const [month, year] = value!.split(' ');
   const monthWithUpperCase = month.charAt(0).toUpperCase() + month.slice(1);
   return props.children?.(value) ?? <>{`${year} ${monthWithUpperCase}`}</>;
};

const useSummaryColumns = (): TableColumnConfiguration<HealthcareProviderNotificationSummary>[] => {
   return useMemo(() => {
      return [
         {
            label: patientPhoneNotificationTranslationKeys.summaryTable.yearAndMonth,
            width: '150px',
            accessor: noop,
            name: 'yearAndMonth',
            renderer: (props: ValueRendererProps<HealthcareProviderNotificationSummary, unknown>) => (
               <YearMonthFormatter value={new Date(props.bean.yearAndMonth)} />
            ),
            sortingDisabled: true,
            sortOrder: SortOrder.ASC,
         },
         {
            label: patientPhoneNotificationTranslationKeys.summaryTable.orderedMessages,
            width: '170px',
            accessor: noop,
            renderer: (props: ValueRendererProps<HealthcareProviderNotificationSummary>) => (
               <label>{props.bean.orderedMessages.valueOf()}</label>
            ),
            name: 'orderedMessages',
            justifyContent: JustifyContent.END,
            textAlign: HorizontalAlign.RIGHT,
            sortingDisabled: true,
         },
      ];
   }, []);
};

const useSummaryDataConfiguration = (): LazyLoadedDataConfigurationByCqrsQuery<HealthcareProviderNotificationSummary> => {
   return useLazyLoadedDataConfigurationByCqrsQuery<HealthcareProviderNotificationSummary>(() => {
      return new HealthcareProviderNotificationSummaryQuery();
   });
};

export const PatientPhoneNotificationSummaryTable = (): JSX.Element => {
   const columns = useSummaryColumns();
   const dataConfiguration = useSummaryDataConfiguration();

   return (
      <StyledMaxWidth maxWidth={480}>
         <StyledPatientPhoneNotificationHeader>
            <Translate>{patientPhoneNotificationTranslationKeys.summary}</Translate>
         </StyledPatientPhoneNotificationHeader>
         <Table<HealthcareProviderNotificationSummary>
            keyExtractor={'yearAndMonth'}
            columns={columns}
            dataConfiguration={dataConfiguration}
            pageSize={10}
            compact
            placeholder={
               <EmptyStatePlaceholder
                  heading={patientPhoneNotificationTranslationKeys.summaryTable.noData}
                  fullLength
                  testId={'noSummary'}
               />
            }
            testId={'summaryTable'}
         />
      </StyledMaxWidth>
   );
};
